import { useLogoutOnServer } from '@/fetching/api/auth';
import { useAxleQuery } from '@/fetching/apiHelpers';
import { OrganizationBasic, organizationBasicSchema } from '@/fetching/sharedSchemas/basicSchemas';
import { atomWithLocalStorage } from '@/utils/browserStorage';
import { useClearLocalData } from '@/utils/clearLocalData';
import { useAtomValue, useSetAtom } from 'jotai';
import { useCallback, useEffect } from 'react';
import { useNavigate } from 'react-router';
import { z } from 'zod';

//////////////////////////////////////////////////////////////////////////////
// region Private atom.
//////////////////////////////////////////////////////////////////////////////

// This is not exposed publicly, as code should only ever modify it using the setters below.
const authStateAtom = atomWithLocalStorage(
    'authenticatedUser',
    null,
    z
        .object({
            // Optional for backwards compatibility: sessions authenticated before
            //  the backend started sending the user UUID have no `uuid` stored, and
            //  a required field here would fail schema validation, wiping the auth
            //  atom and logging the user out.
            uuid: z.string().optional(),
            email: z.string(),
            organization: organizationBasicSchema,
        })
        .nullable(),
);

// endregion
//////////////////////////////////////////////////////////////////////////////
// region Getters.
//////////////////////////////////////////////////////////////////////////////

export const useAuthStateValue = () => useAtomValue(authStateAtom);
export const useSetAuthState = () => useSetAtom(authStateAtom);

// Returns whether the user is authenticated.
//  This is the source of truth for protecting the authenticated routes.
export const useIsAuthenticated = () => !!useAtomValue(authStateAtom);

// This returns null if the user is not logged in.
export const useUserEmailIfLoggedIn = (): string | null => {
    const authentication = useAtomValue(authStateAtom);
    return authentication?.email ?? null;
};

export const useUserEmails = (): {
    currentUserEmail: string | null;
    spoofingUserEmail: string | null;
} => {
    const email = useUserEmailIfLoggedIn();
    if (!email) {
        return {
            spoofingUserEmail: null,
            currentUserEmail: null,
        };
    }
    const segments = email.split('/');
    if (segments.length === 2) {
        return {
            currentUserEmail: segments[1]?.trim() ?? email,
            spoofingUserEmail: segments[0]?.trim() ?? email,
        };
    }

    return {
        currentUserEmail: email,
        spoofingUserEmail: null,
    };
};

// This assumes the user is logged in, otherwise it errors out.
export const useUserEmail = (): string | null => {
    const authState = useAtomValue(authStateAtom);
    if (!authState) {
        throw new Error('User is not authenticated.');
    }
    return authState.email;
};

// This assumes the user is logged in, otherwise it errors out.
export const useUserOrg = (): OrganizationBasic => {
    const authState = useAtomValue(authStateAtom);
    if (!authState) {
        throw new Error('User is not authenticated.');
    }
    return authState.organization;
};

// Returns the organization ID of the logged-in user.
// This assumes the user is logged in, otherwise it errors out.
// This is especially useful when deciding whether to show the "Organization"
//  column in tables. You can hide the organization column if all the rows'
//  organizations are the same as the logged-in user's organization.
export const useUserOrgId = (): string => {
    const userOrg = useUserOrg();
    return userOrg.uuid;
};

// endregion
//////////////////////////////////////////////////////////////////////////////
// region Setters.
//////////////////////////////////////////////////////////////////////////////

// Clears the local authentication state.
// If you want to end a user’s session on the backend, you also have to send
//  a logout request to the backend.
export const useClearAuthenticationState = () => {
    const setAuthentication = useSetAtom(authStateAtom);
    const clearLocalData = useClearLocalData();

    return useCallback(() => {
        // This sets the authentication atom, which is the source of truth for whether
        //  a user is authenticated. This will make the router automatically
        //  redirect to the sign-in page if the user was on a page that required
        //  authentication. It will make any API queries that require authentication
        //  wait until authenticatedUser is set. It’s so nice. Good code.
        setAuthentication(null);

        // Clear any cached queries, atoms, etc. to avoid showing stale data.
        clearLocalData();

        // Q: DO WE NEED TO CLEAR ANY COOKIES?
        //
        // A: Strictly speaking, no.
        //
        // We couldn't clear up the `sessionid` cookie even if we wanted to, since it’s HttpOnly.
        //  But it’s actually okay if the user has an invalid or expired session ID cookie;
        //  the server just ignores it.
        //
        // We could clear up the `csrftoken` cookie here, but it really doesn’t matter.
        //  The back-end handles it just fine if the CSRF token is invalid: it returns a 401 and
        //  the user gets prompted to sign in again, which sets the CSRF token correctly.
    }, [clearLocalData, setAuthentication]);
};

// endregion
//////////////////////////////////////////////////////////////////////////////
// region Miscellaneous stuff.
//////////////////////////////////////////////////////////////////////////////

// Redirect the user onwards once authenticated.
//  This is useful for the login pages.
export const useRedirectOnceAuthenticated = (redirectUrl: string): void => {
    const navigate = useNavigate();
    const isAuthenticated = useIsAuthenticated();

    useEffect(() => {
        if (!isAuthenticated) {
            return;
        }

        // We use replace instead of push so if the user
        //  hits back, it doesn't go to the login page.
        void navigate(redirectUrl, { replace: true });
    }, [isAuthenticated, navigate, redirectUrl]);
};

// This is currently not used anywhere. It's here as a reference
//  for potential use in the future.
//
// This hook returns true once the user is logged in, and we have confirmed we
//  can make an authenticated query successfully. This is safe to call even
//  when the user is logged out, or improperly logged in.
export const useIsAuthenticatedRobust = (): boolean => {
    const isAuthenticated = useIsAuthenticated();
    return useAxleQuery({
        path: '/app-config',
        suspense: false,
        // We won't even try running the query until the local authentication state is set:
        enabled: isAuthenticated,
    }).isReady;
};

// endregion

export const useLogout = (): (() => void) => {
    const logoutOnServer = useLogoutOnServer();
    const clearAuthenticationState = useClearAuthenticationState();

    return useCallback(() => {
        logoutOnServer({})
            .catch(reportError)
            .finally(() => {
                clearAuthenticationState();
            });
    }, [logoutOnServer, clearAuthenticationState]);
};
